import { useEffect, useRef, useState } from "react";
import { PHASE_LABEL, SplitEditor, pctOf } from "@/components/session";
import { Button, ErrorNote, Field, SidePanel, inputClass } from "@/components/ui";
import { ApiError, api, errorMessage } from "@/lib/api";
import { money, percent } from "@/lib/format";
import { useRefreshDirectory } from "@/lib/queries";
import { decimalInput, pctToFraction } from "@/lib/units";
import type { PayoutPreview, SessionDetail } from "@/lib/types";

/*
 * IL PAYOUT IN FUNDED, in due gesti che non si possono fondere.
 *
 * «Ho chiesto il payout» chiude il tratto e mette la Fase in attesa: da lì non
 * si apre più niente, perché il periodo è chiuso e un P&L nuovo non avrebbe
 * dove andare. «Il payout è arrivato» registra l'incasso, lo divide e fa
 * ripartire il conto con una base nuova.
 *
 * Fra i due passano giorni: sono due schermate e non una, come sono due gesti.
 */

type PayoutSession = Pick<SessionDetail, "id" | "phase" | "state"> & {
  clientName: string;
  cycleCurrency: string;
  brokerCurrency: string;
};

/**
 * La Fase, ridotta a quello che serve alle due schermate del payout.
 *
 * Il payout e' un fatto del CICLO: il tipo lo pretende, cosi' chi lo chiama
 * deve aver gia' escluso la Fase senza contabilita' (la rotta la rifiuterebbe
 * con `SESSION_WITHOUT_ACCOUNTING`).
 */
export const payoutSessionOf = (s: SessionDetail & { cycle: NonNullable<SessionDetail["cycle"]> }): PayoutSession => ({
  id: s.id,
  phase: s.phase,
  state: s.state,
  clientName: s.client.name,
  cycleCurrency: s.cycle.currency,
  // Il payout esiste solo in FUNDED, con broker gia' agganciato: il fallback
  // non si legge mai davvero, ed e' qui solo perche' il tipo lo ammette.
  brokerCurrency: s.brokerAccount?.currency ?? s.propAccount.currency,
});

/** Il tasso si chiede solo quando è la chiusura a dirlo: `FX_RATE_MISSING`. */
const chiedeIlTasso = (code: string | null, message: string | null) =>
  code === "FX_RATE_MISSING" || /[Tt]asso .* non disponibile/.test(message ?? "");

// ---------------------------------------------------------------------------
// «Ho chiesto il payout»
// ---------------------------------------------------------------------------

export function AwaitPayoutPanel({ session, onClose }: { session: PayoutSession; onClose: () => void }) {
  const refresh = useRefreshDirectory();
  const [note, setNote] = useState("");
  const [fxRate, setFxRate] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState<string | null>(null);

  const conferma = async () => {
    setBusy(true);
    setError(null);
    setCode(null);
    const rate = fxRate.trim() ? decimalInput(fxRate) : null;
    if (fxRate.trim() && (rate === null || Number(rate) <= 0)) {
      setBusy(false);
      return setError("Il tasso deve essere un numero maggiore di zero");
    }
    try {
      await api.sessions.awaitPayout(session.id, { ...(rate ? { fxRate: rate } : {}), ...(note.trim() ? { note: note.trim() } : {}) });
      await refresh();
      onClose();
    } catch (e) {
      // I rifiuti si mostrano col motivo del backend: coppie aperte, posizioni
      // a mercato e saldo non letto non chiedono la stessa cosa a chi legge.
      setError(errorMessage(e));
      setCode(e instanceof ApiError ? e.code : null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <SidePanel
      open
      onClose={onClose}
      title="A payout"
      subtitle={`${session.clientName} · ${PHASE_LABEL(session.phase)}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button variant="primary" disabled={busy} onClick={() => void conferma()}>
            {busy ? "Registrazione…" : "Conferma: ho chiesto il payout"}
          </Button>
        </>
      }
    >
      <div className="rounded border border-line bg-raised/50 p-3 text-sm text-muted">
        Il tratto di contabilità si chiude adesso, sul saldo del conto broker letto a posizioni chiuse, e la Fase passa a{" "}
        <span className="text-ink">in attesa di payout</span>: fino a quando il bonifico non è arrivato non si apre
        nessuna operazione. Nessun importo viene registrato ora — quanto arriverà non si sa ancora.
      </div>
      {chiedeIlTasso(code, error) && (
        <Field
          label={`Tasso ${session.brokerCurrency}/${session.cycleCurrency}`}
          hint="Né feed del broker né BCE: scrivi il tasso da congelare sulla chiusura del tratto, poi conferma di nuovo."
        >
          {(id) => <input id={id} inputMode="decimal" value={fxRate} placeholder="es. 1.0850" onChange={(e) => setFxRate(e.target.value)} className={inputClass} />}
        </Field>
      )}
      <Field label="Nota">
        {(id) => <textarea id={id} rows={2} value={note} onChange={(e) => setNote(e.target.value)} className={inputClass} />}
      </Field>
      <ErrorNote>{error}</ErrorNote>
    </SidePanel>
  );
}

// ---------------------------------------------------------------------------
// «Il payout è arrivato»
// ---------------------------------------------------------------------------

export function PayoutReceivedPanel({ session, onClose }: { session: PayoutSession; onClose: () => void }) {
  const refresh = useRefreshDirectory();
  const [net, setNet] = useState("");
  const [currency, setCurrency] = useState(session.cycleCurrency);
  const [gross, setGross] = useState("");
  const [propPct, setPropPct] = useState("");
  const [baseAmount, setBaseAmount] = useState("");
  const [baseNote, setBaseNote] = useState("");
  const [fxRate, setFxRate] = useState("");
  const [fxRateCycle, setFxRateCycle] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState<string | null>(null);

  const [preview, setPreview] = useState<PayoutPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  /*
   * Le due quote, come sulla bruciatura: gli IMPORTI sono lo stato e sono
   * quelli che partono al backend. `touched` è vero da quando l'operatore ne
   * muove uno, e da lì non si ricalcolano più da soli — tranne quando cambia
   * il NETTO, che è il numero da cui nascono: due quote che sommano al netto
   * di prima verrebbero rifiutate (`SPLIT_MISMATCH`).
   */
  const [quote, setQuote] = useState<{ client: number; manager: number } | null>(null);
  const touched = useRef(false);

  /*
   * L'anteprima dipende dal netto che l'operatore scrive: si rifà quando quel
   * numero sta fermo mezzo secondo, non a ogni tasto. Legge anche il saldo del
   * conto broker, quindi è una chiamata al gateway: una alla volta.
   */
  const netValue = net.trim() ? decimalInput(net) : null;
  useEffect(() => {
    touched.current = false;
    if (!netValue || !(Number(netValue) > 0)) {
      setPreview(null);
      setQuote(null);
      return;
    }
    let vivo = true;
    const t = setTimeout(async () => {
      try {
        const p = await api.sessions.payoutPreview(session.id, netValue);
        if (!vivo) return;
        setPreview(p);
        setPreviewError(null);
        if (!touched.current) setQuote(p.split ? { client: p.split.client, manager: p.split.manager } : null);
      } catch (e) {
        if (vivo) setPreviewError(errorMessage(e));
      }
    }, 500);
    return () => {
      vivo = false;
      clearTimeout(t);
    };
  }, [session.id, netValue]);

  /**
   * La base a mano serve solo quando il saldo NON SI LEGGE. Con posizioni a
   * mercato (`OPEN_POSITIONS`, `OPEN_LEGS`, `UNREADABLE`) non c'è base da
   * dichiarare: quel saldo comprenderebbe un P&L non ancora realizzato, e il
   * backend rifiuta comunque. Si chiudono le posizioni.
   */
  const baseAMano = preview?.balanceProblem?.code === "BALANCE_NOT_READ";

  const submit = async () => {
    setError(null);
    setCode(null);
    if (!netValue || !(Number(netValue) > 0)) return setError("Scrivi il netto arrivato: è l'unico importo obbligatorio");
    if (currency.trim().length !== 3) return setError("La valuta del payout ha tre lettere, per esempio USD");
    const grossValue = gross.trim() ? decimalInput(gross) : null;
    if (gross.trim() && grossValue === null) return setError("Il lordo deve essere un numero");
    const pctValue = propPct.trim() ? pctToFraction(propPct) : null;
    if (propPct.trim() && pctValue === null) return setError("La percentuale della prop si scrive come numero, per esempio 80");
    const baseValue = baseAmount.trim() ? decimalInput(baseAmount.replace(/^-/, "")) : null;
    if (baseAmount.trim() && baseValue === null) return setError("La base deve essere un numero");
    if (baseValue !== null && baseNote.trim().length < 3) return setError("Una base indicata a mano richiede il motivo");
    const rate = fxRate.trim() ? decimalInput(fxRate) : null;
    const rateCycle = fxRateCycle.trim() ? decimalInput(fxRateCycle) : null;

    setBusy(true);
    try {
      await api.sessions.payoutReceived(session.id, {
        netAmount: netValue,
        currency: currency.trim().toUpperCase(),
        ...(grossValue ? { grossAmount: grossValue } : {}),
        ...(pctValue ? { propSplitPct: pctValue } : {}),
        // Gli IMPORTI come l'operatore li ha confermati, e la percentuale solo
        // come contesto: è quella che questi due numeri realizzano, non una
        // percentuale riapplicata a un netto che nel frattempo è cambiato.
        ...(quote ? { split: { client: quote.client, manager: quote.manager, pct: pctOf(quote.client, quote.client + quote.manager) } } : {}),
        ...(baseValue !== null
          ? { baseAmount: baseAmount.trim().startsWith("-") ? `-${baseValue}` : baseValue, baseNote: baseNote.trim() }
          : {}),
        ...(rate ? { fxRate: rate } : {}),
        ...(rateCycle ? { fxRateCycle: rateCycle } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      await refresh();
      onClose();
    } catch (e) {
      setError(errorMessage(e));
      setCode(e instanceof ApiError ? e.code : null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <SidePanel
      open
      onClose={onClose}
      title="Payout arrivato"
      subtitle={`${session.clientName} · ${PHASE_LABEL(session.phase)}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button variant="primary" disabled={busy} onClick={() => void submit()}>
            {busy ? "Registrazione…" : "Conferma il payout arrivato"}
          </Button>
        </>
      }
    >
      <div className="rounded border border-line bg-raised/50 p-3 text-sm text-muted">
        Il netto entra nel registro e si divide in due quote; il conto riparte da una base nuova, e da lì «da recuperare»
        guarda solo il tratto nuovo: fee, rettifiche e perdite di prima le ha pagate questo payout.
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <Field label="Netto arrivato" hint="L'unico importo obbligatorio: quello che è stato accreditato davvero.">
            {(id) => <input id={id} inputMode="decimal" value={net} onChange={(e) => setNet(e.target.value)} className={inputClass} />}
          </Field>
        </div>
        <Field label="Valuta">
          {(id) => <input id={id} value={currency} maxLength={3} onChange={(e) => setCurrency(e.target.value.toUpperCase())} className={inputClass} />}
        </Field>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <Field label="Lordo" hint="Facoltativo. Vuoto se non si conosce: uguagliarlo al netto direbbe che la prop non ha trattenuto niente.">
            {(id) => <input id={id} inputMode="decimal" value={gross} onChange={(e) => setGross(e.target.value)} className={inputClass} />}
          </Field>
        </div>
        <Field label="% della prop" hint="Facoltativa.">
          {(id) => (
            <div className="relative">
              <input id={id} inputMode="decimal" value={propPct} onChange={(e) => setPropPct(e.target.value)} className={`${inputClass} pr-8`} />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-faint">%</span>
            </div>
          )}
        </Field>
      </div>

      {previewError ? (
        <ErrorNote>{previewError}</ErrorNote>
      ) : preview ? (
        <>
          <PayoutSummary p={preview} />
          {preview.split ? (
            <SplitEditor
              net={preview.netAmount}
              /* Le quote sono nella valuta del PAYOUT, quella scritta qui sopra: è
                 l'importo arrivato che si divide, non un numero del ciclo. */
              currency={currency.trim().toUpperCase() || preview.currency}
              quote={quote}
              defaultPct={preview.payoutSplitPct}
              onQuote={(q) => {
                touched.current = true;
                setQuote(q);
              }}
            />
          ) : (
            <p className="text-xs text-faint">Niente da dividere: lo split nasce da un netto positivo.</p>
          )}
        </>
      ) : (
        <p className="text-sm text-faint">Scrivi il netto: le due quote e la base del tratto nuovo compaiono qui.</p>
      )}

      {baseAMano && (
        <div className="space-y-3 rounded border border-warn/40 bg-warn/5 p-3">
          <p className="text-sm text-ink">
            Il saldo del conto broker non si è letto: la base del tratto nuovo va dichiarata, con il motivo.
          </p>
          <Field label={`Base del tratto nuovo (${session.brokerCurrency})`}>
            {(id) => <input id={id} inputMode="decimal" value={baseAmount} onChange={(e) => setBaseAmount(e.target.value)} className={inputClass} />}
          </Field>
          <Field label="Motivo della base">
            {(id) => <input id={id} value={baseNote} onChange={(e) => setBaseNote(e.target.value)} className={inputClass} />}
          </Field>
        </div>
      )}

      {chiedeIlTasso(code, error) && (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={`Tasso ${currency}/reporting`} hint="Per la riga del registro.">
            {(id) => <input id={id} inputMode="decimal" value={fxRate} placeholder="es. 0.9200" onChange={(e) => setFxRate(e.target.value)} className={inputClass} />}
          </Field>
          <Field label={`Tasso ${currency}/${session.cycleCurrency}`} hint="Per i numeri del ciclo.">
            {(id) => <input id={id} inputMode="decimal" value={fxRateCycle} placeholder="es. 1.0850" onChange={(e) => setFxRateCycle(e.target.value)} className={inputClass} />}
          </Field>
        </div>
      )}

      <Field label="Nota">
        {(id) => <textarea id={id} rows={2} value={note} onChange={(e) => setNote(e.target.value)} className={inputClass} />}
      </Field>
      <ErrorNote>{error}</ErrorNote>
    </SidePanel>
  );
}

/**
 * Che cosa regola questo payout: il tratto che chiude, quanto c'era da
 * recuperare prima, e la base da cui il conto riparte. Le due quote no: si
 * modificano, e stanno nel loro editor.
 *
 * Ogni importo con la sua valuta — queste sono in valuta del CICLO, non in
 * quella del payout, che può essere un'altra.
 */
function PayoutSummary({ p }: { p: PayoutPreview }) {
  return (
    <div className="space-y-2 rounded border border-line bg-raised/50 p-3 text-sm">
      <p className="text-xs text-faint">
        Percentuale congelata sul ciclo: {percent(p.payoutSplitPct, 0)} al cliente. È il punto di partenza delle due
        quote qui sotto, non un vincolo.
      </p>
      {p.period && (
        <p className="text-muted">
          Chiude il tratto n. {p.period.seq}: netto {money(p.period.netCycle, p.currency)}
          {p.period.netNative !== null && p.period.currency !== p.currency ? ` (${money(p.period.netNative, p.period.currency)})` : ""}
        </p>
      )}
      {p.recoverBefore !== null && (
        <p className="text-muted" title="Da qui in poi «da recuperare» guarda solo il tratto nuovo">
          Da recuperare prima del payout: {money(p.recoverBefore, p.currency)}
        </p>
      )}
      {p.newBase ? (
        <p className="text-muted">
          Base del tratto nuovo: {money(p.newBase.amount, p.newBase.currency)} <span className="text-xs text-faint">misurata adesso</span>
        </p>
      ) : (
        <p className="text-warn">{p.balanceProblem?.message ?? "Il saldo del conto broker non si è letto."}</p>
      )}
    </div>
  );
}
